import { InputHTMLAttributes, forwardRef, ReactNode, useId } from 'react';
import { Check } from 'lucide-react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: boolean;
  size?: 'sm' | 'md';
}

/**
 * Custom checkbox. Natywny input (accessibility) + wizualna replika (dark mode
 * friendly). Etykieta klikalna przez htmlFor.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, error, size = 'md', className, disabled, id: idProp, checked, ...rest },
  ref,
) {
  const autoId = useId();
  const id = idProp ?? autoId;

  const boxBase = [
    'shrink-0 rounded-[4px] border transition-colors duration-fast ease-smooth flex items-center justify-center',
    size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4',
  ].join(' ');
  const boxState = error
    ? 'border-danger/60 bg-surface-base'
    : checked
      ? 'bg-accent-primary border-accent-primary'
      : 'border-border bg-surface-base hover:border-accent-primary/50';
  const wrapperDisabled = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';

  return (
    <label
      htmlFor={id}
      className={['inline-flex items-start group', size === 'sm' ? 'gap-2' : 'gap-2.5', wrapperDisabled, className].filter(Boolean).join(' ')}
    >
      <span className="relative inline-flex items-center">
        <input
          ref={ref}
          type="checkbox"
          id={id}
          checked={checked}
          disabled={disabled}
          className="peer sr-only"
          {...rest}
        />
        <span
          className={[
            boxBase,
            boxState,
            'peer-focus-visible:ring-2 peer-focus-visible:ring-accent-primary/40 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-base',
          ].join(' ')}
          aria-hidden="true"
        >
          {checked && <Check className={size === 'sm' ? 'h-2.5 w-2.5 text-white' : 'h-3 w-3 text-white'} strokeWidth={3} />}
        </span>
      </span>
      {(label || description) && (
        <span className="flex flex-col min-w-0">
          {label && (
            <span className={`${size === 'sm' ? 'text-xs' : 'text-sm'} text-text-primary leading-tight select-none`}>{label}</span>
          )}
          {description && (
            <span className={`${size === 'sm' ? 'text-[10px]' : 'text-xs'} text-text-muted leading-snug mt-0.5`}>{description}</span>
          )}
        </span>
      )}
    </label>
  );
});
